import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  fetchAdminUser,
  fetchGalleryPublicFeed,
  fetchGallerySiteCandidates,
  hideGallerySubmission,
  restoreGallerySubmission,
  deleteGallerySubmission,
  publishGallerySiteCandidate,
  clearGallerySubmissionPreview,
  updateGallerySubmissionPreview,
  uploadGallerySubmissionMedia,
  type GallerySubmission,
  type GallerySiteCandidate,
  type User,
} from '@/admin/lib/api';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/admin/components/ui/alert-dialog';
import { Input } from '@/admin/components/ui/input';
import { Textarea } from '@/admin/components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/admin/components/ui/tabs';
import { Skeleton } from '@/admin/components/ui/skeleton';
import { cn } from '@/admin/lib/utils';
import {
  IconPhoto,
  IconRefresh,
  IconTrash,
  IconEye,
  IconEyeOff,
  IconCheck,
  IconExternalLink,
  IconHeart,
  IconUser,
  IconX,
} from '@tabler/icons-react';

type GalleryTab = 'published' | 'sites';

const GALLERY_ADMIN_PAGE_SIZE = 100;
const loadingSkeletonRows = ['row-1', 'row-2', 'row-3', 'row-4'];

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'published':
      return (
        <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-0 font-medium">
          Published
        </Badge>
      );
    case 'prechecking':
      return (
        <Badge className="bg-sky-500/15 text-sky-600 dark:text-sky-400 border-0 font-medium">
          Prechecking
        </Badge>
      );
    case 'submitted':
      return (
        <Badge variant="outline" className="text-muted-foreground font-medium">
          Submitted
        </Badge>
      );
    case 'hidden':
      return (
        <Badge variant="secondary" className="font-medium">
          Hidden
        </Badge>
      );
    case 'rejected':
      return (
        <Badge className="bg-rose-500/15 text-rose-600 dark:text-rose-400 border-0 font-medium">
          Rejected
        </Badge>
      );
    case 'deleted':
      return (
        <Badge className="bg-muted text-muted-foreground border-0 font-medium">
          Deleted
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground font-medium">
          {status || 'Unknown'}
        </Badge>
      );
  }
}

function CoverThumb({ url, alt }: { url?: string; alt: string }) {
  if (!url) {
    return (
      <div className="flex h-12 w-20 items-center justify-center rounded-md border border-dashed border-border bg-muted text-muted-foreground">
        <IconPhoto className="size-4" />
      </div>
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={url}
      alt={alt}
      className="h-12 w-20 rounded-md border border-border object-cover bg-muted"
    />
  );
}

function MediaPreview({
  title,
  coverURL,
  videoURL,
  className = '',
}: {
  title: string;
  coverURL?: string;
  videoURL?: string;
  className?: string;
}) {
  const video = videoURL?.trim();
  const cover = coverURL?.trim();
  if (video) {
    return (
      <video
        src={video}
        className={`size-full object-cover ${className}`}
        muted
        playsInline
      />
    );
  }
  if (cover) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={cover}
        alt={title}
        className={`size-full object-cover ${className}`}
      />
    );
  }
  return (
    <div
      className={`flex size-full flex-col items-center justify-center gap-2 bg-muted/30 text-muted-foreground ${className}`}
    >
      <IconPhoto className="size-7" />
      <span className="text-xs">No media</span>
    </div>
  );
}

function SourceLink({ url }: { url: string }) {
  if (!url) return <span className="text-muted-foreground">—</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:underline"
    >
      <span className="max-w-[28ch] truncate">{url}</span>
      <IconExternalLink className="size-3.5 shrink-0" />
    </a>
  );
}

function previewFrameCount(item: GallerySubmission): number {
  return item.preview_frame_asset_urls?.length ?? 0;
}

function previewAssetsText(item: GallerySubmission): string {
  return JSON.stringify(item.preview_assets ?? {}, null, 2);
}

function stringPreviewAsset(
  assets: Record<string, unknown> | undefined,
  key: string
): string {
  const value = assets?.[key];
  return typeof value === 'string' ? value : '';
}

function previewVideoURL(item: GallerySubmission): string {
  return (
    stringPreviewAsset(item.preview_assets, 'video_asset_url') ||
    stringPreviewAsset(item.preview_assets, 'video_url')
  );
}

function shortId(id: string | undefined): string {
  if (!id) return '';
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

function ownerDisplayLabel(
  ownerId: string,
  ownerById: Map<string, User>
): string {
  const owner = ownerById.get(ownerId);
  return (
    owner?.display_name?.trim() ||
    owner?.email ||
    (ownerId ? `owner ${shortId(ownerId)}` : 'Unknown owner')
  );
}

function submissionOwnerId(item: GallerySubmission): string {
  return item.source_ref?.owner_id || item.creator_display_name || 'unknown';
}

function groupSubmissionsByOwner(
  items: GallerySubmission[],
  ownerById: Map<string, User>
) {
  const groups = new Map<
    string,
    { ownerId: string; label: string; items: GallerySubmission[] }
  >();
  for (const item of items) {
    const ownerId = submissionOwnerId(item);
    const existing = groups.get(ownerId);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    groups.set(ownerId, {
      ownerId,
      label:
        ownerId === 'unknown'
          ? 'Unknown owner'
          : ownerDisplayLabel(ownerId, ownerById),
      items: [item],
    });
  }
  return Array.from(groups.values());
}

function groupSiteCandidatesByOwner(
  items: GallerySiteCandidate[],
  ownerById: Map<string, User>
) {
  const groups = new Map<
    string,
    { ownerId: string; label: string; items: GallerySiteCandidate[] }
  >();
  for (const item of items) {
    const ownerId = item.owner_user_id || 'unknown';
    const existing = groups.get(ownerId);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    groups.set(ownerId, {
      ownerId,
      label:
        ownerId === 'unknown'
          ? 'Unknown owner'
          : siteCandidateOwnerLabel(item, ownerById),
      items: [item],
    });
  }
  return Array.from(groups.values());
}

function siteCandidateOwnerLabel(
  item: GallerySiteCandidate,
  ownerById: Map<string, User>
): string {
  return (
    item.owner_display_name?.trim() ||
    item.owner_email?.trim() ||
    ownerDisplayLabel(item.owner_user_id, ownerById)
  );
}

function OwnerGroupRow({
  label,
  count,
  colSpan,
}: {
  label: string;
  count: number;
  colSpan: number;
}) {
  return (
    <TableRow className="bg-muted/40 hover:bg-muted/40">
      <TableCell colSpan={colSpan} className="py-2">
        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <span className="truncate font-medium">{label}</span>
          <span className="shrink-0">{count} items</span>
        </div>
      </TableCell>
    </TableRow>
  );
}

function CreatorCell({
  item,
  ownerById,
}: {
  item: GallerySubmission;
  ownerById: Map<string, User>;
}) {
  const ownerId = item.source_ref?.owner_id ?? '';
  const ownerRecord = ownerId ? ownerById.get(ownerId) : undefined;

  const name =
    item.creator_display_name?.trim() ||
    ownerRecord?.display_name?.trim() ||
    '';
  const avatar =
    item.creator_avatar_url?.trim() || ownerRecord?.avatar_url?.trim() || '';
  const email = ownerRecord?.email ?? '';
  const agentId = item.source_ref?.agent_id;

  // Display priority: creator_display_name → user.display_name → email → owner-id short.
  const primary =
    name ||
    email ||
    (ownerId && `owner ${shortId(ownerId)}`) ||
    (agentId && `agent ${shortId(agentId)}`) ||
    '(unknown creator)';
  const isFallback = !name && !email;

  return (
    <div className="flex min-w-0 items-center gap-2">
      {avatar ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={avatar}
          alt={name || 'creator avatar'}
          className="size-7 shrink-0 rounded-full border border-border object-cover bg-muted"
        />
      ) : (
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full border border-dashed border-border bg-muted text-muted-foreground">
          <IconUser className="size-3.5" />
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        <span
          className={`truncate text-sm ${
            isFallback ? 'italic text-muted-foreground' : 'font-medium'
          }`}
          title={ownerId || undefined}
        >
          {primary}
        </span>
        {!name && email ? (
          <span
            className="truncate text-[11px] text-muted-foreground"
            title={ownerId}
          >
            {ownerId ? shortId(ownerId) : ''}
          </span>
        ) : item.source_ref?.site_name ? (
          <span className="truncate font-mono text-[11px] text-muted-foreground">
            {item.source_ref.site_name}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export const GalleryPage: React.FC = () => {
  const [tab, setTab] = useState<GalleryTab>('published');
  const [publishedItems, setPublishedItems] = useState<GallerySubmission[]>([]);
  const [siteCandidates, setSiteCandidates] = useState<GallerySiteCandidate[]>(
    []
  );
  const [publishedCursor, setPublishedCursor] = useState<string | null>(null);
  const [publishedHasMore, setPublishedHasMore] = useState(false);
  const [siteCandidatesOffset, setSiteCandidatesOffset] = useState(0);
  const [siteCandidatesHasMore, setSiteCandidatesHasMore] = useState(false);
  const [ownerById, setOwnerById] = useState<Map<string, User>>(new Map());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMorePublished, setLoadingMorePublished] = useState(false);
  const [loadingMoreSiteCandidates, setLoadingMoreSiteCandidates] =
    useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState<
    'cover' | 'video' | null
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<GallerySubmission | null>(
    null
  );
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [previewDraft, setPreviewDraft] = useState<{
    coverURL: string;
    videoURL: string;
    framesText: string;
    assetsText: string;
  } | null>(null);

  const selectedItem = useMemo(
    () => publishedItems.find(item => item.id === selectedItemId) ?? null,
    [publishedItems, selectedItemId]
  );
  const [actionError, setActionError] = useState<string | null>(null);

  const selectItem = useCallback((item: GallerySubmission) => {
    setSelectedItemId(item.id);
    setPreviewDraft({
      coverURL: item.cover_asset_url ?? '',
      videoURL: previewVideoURL(item),
      framesText: (item.preview_frame_asset_urls ?? []).join('\n'),
      assetsText: previewAssetsText(item),
    });
  }, []);

  const hydrateOwnerIds = useCallback(async (ids: string[]) => {
    const ownerIds = new Set<string>();
    for (const id of ids) {
      if (id) ownerIds.add(id);
    }
    if (ownerIds.size === 0) return;

    // Only fetch unknown owners; reuse cached ones for refresh cycles.
    const unknown: string[] = [];
    setOwnerById(prev => {
      for (const id of ownerIds) {
        if (!prev.has(id)) unknown.push(id);
      }
      return prev;
    });
    if (unknown.length === 0) return;

    const resolved = await Promise.allSettled(
      unknown.map(async id => [id, await fetchAdminUser(id)] as const)
    );
    setOwnerById(prev => {
      const next = new Map(prev);
      for (const r of resolved) {
        if (r.status === 'fulfilled') {
          const [id, user] = r.value;
          next.set(id, user);
        }
      }
      return next;
    });
  }, []);

  const hydrateOwners = useCallback(
    async (submissions: GallerySubmission[]) => {
      await hydrateOwnerIds(
        submissions.map(sub => sub.source_ref?.owner_id ?? '').filter(Boolean)
      );
    },
    [hydrateOwnerIds]
  );

  const loadAll = useCallback(async () => {
    try {
      setError(null);
      const [publicPage, siteResp] = await Promise.all([
        fetchGalleryPublicFeed(GALLERY_ADMIN_PAGE_SIZE),
        fetchGallerySiteCandidates(GALLERY_ADMIN_PAGE_SIZE, 0),
      ]);
      const publicList = publicPage.submissions;
      const sites = siteResp.candidates ?? [];
      setPublishedItems(publicList);
      setPublishedCursor(publicPage.next_cursor ?? null);
      setPublishedHasMore(Boolean(publicPage.has_more));
      setSiteCandidates(sites);
      setSiteCandidatesOffset(sites.length);
      setSiteCandidatesHasMore(Boolean(siteResp.has_more));
      // Fire-and-forget owner hydration; admin lookups are best-effort and
      // a 4xx on a single user shouldn't block the table from rendering.
      void hydrateOwners(publicList);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load gallery');
    }
  }, [hydrateOwners]);

  useEffect(() => {
    setLoading(true);
    loadAll().finally(() => setLoading(false));
  }, [loadAll]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
  }, [loadAll]);

  const loadMorePublished = useCallback(async () => {
    if (!publishedCursor || loadingMorePublished) return;
    setLoadingMorePublished(true);
    setActionError(null);
    try {
      const page = await fetchGalleryPublicFeed(
        GALLERY_ADMIN_PAGE_SIZE,
        publishedCursor
      );
      setPublishedItems(prev => [...prev, ...page.submissions]);
      setPublishedCursor(page.next_cursor ?? null);
      setPublishedHasMore(Boolean(page.has_more));
      void hydrateOwners(page.submissions);
    } catch (e) {
      setActionError(
        `Load more published items failed: ${
          e instanceof Error ? e.message : String(e)
        }`
      );
    } finally {
      setLoadingMorePublished(false);
    }
  }, [hydrateOwners, loadingMorePublished, publishedCursor]);

  const loadMoreSiteCandidates = useCallback(async () => {
    if (!siteCandidatesHasMore || loadingMoreSiteCandidates) return;
    setLoadingMoreSiteCandidates(true);
    setActionError(null);
    try {
      const page = await fetchGallerySiteCandidates(
        GALLERY_ADMIN_PAGE_SIZE,
        siteCandidatesOffset
      );
      const nextItems = page.candidates ?? [];
      setSiteCandidates(prev => [...prev, ...nextItems]);
      setSiteCandidatesOffset(prev => prev + nextItems.length);
      setSiteCandidatesHasMore(Boolean(page.has_more));
    } catch (e) {
      setActionError(
        `Load more site candidates failed: ${
          e instanceof Error ? e.message : String(e)
        }`
      );
    } finally {
      setLoadingMoreSiteCandidates(false);
    }
  }, [loadingMoreSiteCandidates, siteCandidatesHasMore, siteCandidatesOffset]);

  const replaceById = useCallback((next: GallerySubmission) => {
    setPublishedItems(prev => {
      const without = prev.filter(item => item.id !== next.id);
      if (next.status === 'published') {
        return [next, ...without];
      }
      return without;
    });
  }, []);

  const removeById = useCallback((id: string) => {
    setPublishedItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const runAction = useCallback(
    async (
      id: string,
      label: string,
      action: () => Promise<GallerySubmission | void>
    ) => {
      setPendingActionId(id);
      setActionError(null);
      try {
        const next = await action();
        if (next) {
          replaceById(next);
        } else {
          removeById(id);
        }
      } catch (e) {
        setActionError(
          `${label} failed: ${e instanceof Error ? e.message : String(e)}`
        );
      } finally {
        setPendingActionId(null);
      }
    },
    [removeById, replaceById]
  );

  const publishCandidate = useCallback(
    async (candidate: GallerySiteCandidate) => {
      setPendingActionId(candidate.id);
      setActionError(null);
      try {
        const submission = await publishGallerySiteCandidate({
          agent_id: candidate.agent_id,
          site_name: candidate.site_name,
        });
        setSiteCandidates(prev =>
          prev.map(item =>
            item.id === candidate.id
              ? {
                  ...item,
                  already_in_book: true,
                  submission_id: submission.id,
                  submission_status: submission.status,
                }
              : item
          )
        );
        replaceById(submission);
        void hydrateOwners([submission]);
      } catch (e) {
        setActionError(
          `Publish to Book failed: ${e instanceof Error ? e.message : String(e)}`
        );
      } finally {
        setPendingActionId(null);
      }
    },
    [hydrateOwners, replaceById]
  );

  const savePreview = useCallback(
    async (item: GallerySubmission) => {
      if (!previewDraft) return;
      let parsedAssets: Record<string, unknown> = {};
      try {
        parsedAssets = previewDraft.assetsText.trim()
          ? JSON.parse(previewDraft.assetsText)
          : {};
      } catch (e) {
        setActionError(
          `Preview assets JSON is invalid: ${e instanceof Error ? e.message : String(e)}`
        );
        return;
      }
      delete parsedAssets.video_url;
      if (previewDraft.videoURL.trim()) {
        parsedAssets.video_asset_url = previewDraft.videoURL.trim();
      } else {
        delete parsedAssets.video_asset_url;
      }
      const frames = previewDraft.framesText
        .split(/\r?\n/)
        .map(frame => frame.trim())
        .filter(Boolean);
      await runAction(item.id, 'Update preview', () =>
        updateGallerySubmissionPreview(item.id, {
          cover_asset_url: previewDraft.coverURL.trim(),
          preview_frame_asset_urls: frames,
          preview_assets: parsedAssets,
          reason: 'admin updated main preview',
        })
      );
    },
    [previewDraft, runAction]
  );

  const clearPreview = useCallback(
    async (item: GallerySubmission) => {
      await runAction(item.id, 'Clear preview', () =>
        clearGallerySubmissionPreview(item.id, 'admin cleared main preview')
      );
      setPreviewDraft({
        coverURL: '',
        videoURL: '',
        framesText: '',
        assetsText: '{}',
      });
    },
    [runAction]
  );

  const uploadPreviewMedia = useCallback(
    async (item: GallerySubmission, file: File, type: 'cover' | 'video') => {
      setPendingActionId(item.id);
      setUploadingMedia(type);
      setActionError(null);
      try {
        const result = await uploadGallerySubmissionMedia(item.id, file, type);
        replaceById(result.submission);
        setSelectedItemId(result.submission.id);
        setPreviewDraft({
          coverURL: result.submission.cover_asset_url ?? '',
          videoURL: previewVideoURL(result.submission),
          framesText: (result.submission.preview_frame_asset_urls ?? []).join(
            '\n'
          ),
          assetsText: previewAssetsText(result.submission),
        });
      } catch (e) {
        setActionError(
          `Upload ${type} failed: ${e instanceof Error ? e.message : String(e)}`
        );
      } finally {
        setUploadingMedia(null);
        setPendingActionId(null);
      }
    },
    [replaceById]
  );

  useEffect(() => {
    if (!selectedItemId || selectedItem) return;
    setSelectedItemId(null);
    setPreviewDraft(null);
  }, [selectedItem, selectedItemId]);

  // Total displayed across tabs so the header chip mirrors the
  // "<n> total" subtitle other admin pages use (skills.tsx:355). Site
  // candidates and published submissions are disjoint sets so the sum
  // is meaningful.
  const totalDisplayed = publishedItems.length + siteCandidates.length;

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-background">
      {/* Header — mirror skills.tsx:347-382: icon-square + title + total
          subtitle on left, icon-only refresh on right. Anchors the page to
          the rest of admin instead of feeling like a one-off CRUD form. */}
      <div className="flex items-center justify-between border-b bg-card px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <IconPhoto className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Book / Gallery</h1>
            <p className="text-xs text-muted-foreground">
              {totalDisplayed} item{totalDisplayed === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            aria-label="Refresh"
          >
            <IconRefresh
              className={`h-4 w-4 ${refreshing || loading ? 'animate-spin' : ''}`}
            />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 overflow-hidden p-6">
        {error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {actionError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {actionError}
          </div>
        ) : null}

        <Tabs
          value={tab}
          onValueChange={value => setTab(value as GalleryTab)}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <TabsList className="self-start">
            <TabsTrigger value="published">
              Published
              <span className="ml-2 rounded-full bg-muted px-1.5 text-xs">
                {publishedItems.length}
                {publishedHasMore ? '+' : ''}
              </span>
            </TabsTrigger>
            <TabsTrigger value="sites">
              Site candidates
              <span className="ml-2 rounded-full bg-muted px-1.5 text-xs">
                {siteCandidates.length}
                {siteCandidatesHasMore ? '+' : ''}
              </span>
            </TabsTrigger>
          </TabsList>

          {tab === 'sites' ? (
            <TabsContent
              value="sites"
              className="mt-4 flex flex-1 flex-col overflow-hidden"
            >
              <SiteCandidatesTable
                items={siteCandidates}
                ownerById={ownerById}
                loading={loading}
                pendingActionId={pendingActionId}
                onPublish={candidate => void publishCandidate(candidate)}
              />
              {siteCandidatesHasMore ? (
                <div className="mt-3 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={loadingMoreSiteCandidates}
                    onClick={() => void loadMoreSiteCandidates()}
                  >
                    {loadingMoreSiteCandidates ? 'Loading…' : 'Load more sites'}
                  </Button>
                </div>
              ) : null}
            </TabsContent>
          ) : (
            <TabsContent
              value="published"
              className="mt-4 flex flex-1 flex-col overflow-hidden"
            >
              {/* Only reserve the right column when an item is actually
                  selected. Showing an always-empty "Select an item"
                  placeholder ate ~30% of the page width and made the
                  layout read as broken. */}
              <div
                className={cn(
                  'grid min-h-0 flex-1 gap-4',
                  selectedItem && 'xl:grid-cols-[minmax(0,1fr)_380px]'
                )}
              >
                <SubmissionsTable
                  items={publishedItems}
                  ownerById={ownerById}
                  loading={loading}
                  pendingActionId={pendingActionId}
                  selectedItemId={selectedItemId}
                  onHide={item =>
                    runAction(item.id, 'Hide', () =>
                      hideGallerySubmission(item.id)
                    )
                  }
                  onRestore={item =>
                    runAction(item.id, 'Restore', () =>
                      restoreGallerySubmission(item.id)
                    )
                  }
                  onDelete={item => setConfirmDelete(item)}
                  onSelect={selectItem}
                />
                {selectedItem && previewDraft ? (
                  <PreviewInspector
                    item={selectedItem}
                    draft={previewDraft}
                    busy={pendingActionId === selectedItem.id}
                    onDraftChange={setPreviewDraft}
                    onSave={item => void savePreview(item)}
                    onClear={item => void clearPreview(item)}
                    onUpload={(item, file, type) =>
                      void uploadPreviewMedia(item, file, type)
                    }
                    uploadingMedia={uploadingMedia}
                    onClose={() => {
                      setSelectedItemId(null);
                      setPreviewDraft(null);
                    }}
                  />
                ) : null}
              </div>
              {publishedHasMore ? (
                <div className="mt-3 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={loadingMorePublished}
                    onClick={() => void loadMorePublished()}
                  >
                    {loadingMorePublished
                      ? 'Loading…'
                      : 'Load more published items'}
                  </Button>
                </div>
              ) : null}
            </TabsContent>
          )}
        </Tabs>

        <AlertDialog
          open={confirmDelete !== null}
          onOpenChange={open => !open && setConfirmDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Permanently delete submission?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This soft-deletes the submission and removes it from the public
                feed. It cannot be restored from this UI.
                {confirmDelete ? (
                  <span className="mt-2 block font-mono text-xs">
                    {confirmDelete.id}
                  </span>
                ) : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (!confirmDelete) return;
                  const id = confirmDelete.id;
                  setConfirmDelete(null);
                  void runAction(id, 'Delete', async () => {
                    await deleteGallerySubmission(id);
                  });
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

function SubmissionsTable({
  items,
  ownerById,
  loading,
  pendingActionId,
  selectedItemId,
  onHide,
  onRestore,
  onDelete,
  onSelect,
}: {
  items: GallerySubmission[];
  ownerById: Map<string, User>;
  loading: boolean;
  pendingActionId: string | null;
  selectedItemId: string | null;
  onHide: (item: GallerySubmission) => void;
  onRestore: (item: GallerySubmission) => void;
  onDelete: (item: GallerySubmission) => void;
  onSelect: (item: GallerySubmission) => void;
}) {
  const groups = useMemo(
    () => groupSubmissionsByOwner(items, ownerById),
    [items, ownerById]
  );

  if (loading) {
    return (
      <div className="space-y-2">
        {loadingSkeletonRows.map(row => (
          <Skeleton key={row} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-md border border-dashed border-border p-12 text-sm text-muted-foreground">
        No published items.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[120px]">Cover</TableHead>
            <TableHead className="min-w-[260px]">Title</TableHead>
            <TableHead className="min-w-[160px]">Main preview</TableHead>
            <TableHead className="min-w-[200px]">Creator</TableHead>
            <TableHead className="min-w-[220px]">Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[80px] text-center">Likes</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map(group => (
            <React.Fragment key={group.ownerId}>
              <OwnerGroupRow
                label={group.label}
                count={group.items.length}
                colSpan={8}
              />
              {group.items.map(item => {
                const isPending = pendingActionId === item.id;
                return (
                  <TableRow
                    key={item.id}
                    className={
                      selectedItemId === item.id
                        ? 'bg-muted/50 hover:bg-muted/50'
                        : undefined
                    }
                  >
                    <TableCell>
                      <CoverThumb
                        url={item.cover_asset_url}
                        alt={`${item.title} cover`}
                      />
                    </TableCell>
                    <TableCell className="max-w-[36ch]">
                      <div className="truncate font-medium">
                        {item.title || '(untitled)'}
                      </div>
                      {item.caption ? (
                        <div
                          className="mt-0.5 line-clamp-2 text-xs text-muted-foreground"
                          title={item.caption}
                        >
                          {item.caption}
                        </div>
                      ) : null}
                      <div className="mt-1 flex items-center gap-2">
                        <span
                          className="font-mono text-[11px] text-muted-foreground"
                          title={item.id}
                        >
                          {shortId(item.id)}
                        </span>
                        {item.public_url ? (
                          <a
                            href={item.public_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                          >
                            public <IconExternalLink className="size-3" />
                          </a>
                        ) : null}
                        {item.changed_since_review ? (
                          <Badge
                            variant="outline"
                            className="px-1 py-0 text-[10px] font-medium"
                          >
                            changed
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 text-xs">
                        {item.cover_asset_url ? (
                          <a
                            href={item.cover_asset_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            Cover <IconExternalLink className="size-3" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">
                            No cover
                          </span>
                        )}
                        {previewVideoURL(item) ? (
                          <a
                            href={previewVideoURL(item)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            Video <IconExternalLink className="size-3" />
                          </a>
                        ) : null}
                        <span className="text-muted-foreground">
                          {previewFrameCount(item)} frame
                          {previewFrameCount(item) === 1 ? '' : 's'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <CreatorCell item={item} ownerById={ownerById} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <SourceLink url={item.source_url} />
                        {item.source_ref?.type ? (
                          <Badge
                            variant="outline"
                            className="w-fit px-1.5 py-0 font-mono text-[10px]"
                          >
                            {item.source_ref.type}
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={item.status} />
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                        <IconHeart className="size-3.5" />
                        {item.like_count ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={isPending}
                          onClick={() => onSelect(item)}
                        >
                          Edit
                        </Button>
                        {item.status === 'published' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isPending}
                            onClick={() => onHide(item)}
                          >
                            <IconEyeOff className="mr-1 size-4" /> Hide
                          </Button>
                        ) : null}
                        {item.status === 'hidden' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isPending}
                            onClick={() => onRestore(item)}
                          >
                            <IconEye className="mr-1 size-4" /> Restore
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={isPending}
                          onClick={() => onDelete(item)}
                          className="text-destructive hover:text-destructive"
                        >
                          <IconTrash className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PreviewInspector({
  item,
  draft,
  busy,
  onDraftChange,
  onSave,
  onClear,
  onUpload,
  uploadingMedia,
  onClose,
}: {
  item: GallerySubmission | null;
  draft: {
    coverURL: string;
    videoURL: string;
    framesText: string;
    assetsText: string;
  } | null;
  busy: boolean;
  onDraftChange: React.Dispatch<
    React.SetStateAction<{
      coverURL: string;
      videoURL: string;
      framesText: string;
      assetsText: string;
    } | null>
  >;
  onSave: (item: GallerySubmission) => void;
  onClear: (item: GallerySubmission) => void;
  onUpload: (
    item: GallerySubmission,
    file: File,
    type: 'cover' | 'video'
  ) => void;
  uploadingMedia: 'cover' | 'video' | null;
  onClose: () => void;
}) {
  const coverInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const updateDraft = (patch: Partial<NonNullable<typeof draft>>) => {
    onDraftChange(prev => (prev ? { ...prev, ...patch } : prev));
  };

  if (!item || !draft) {
    return (
      <aside className="hidden w-full shrink-0 flex-col border-l bg-card xl:flex">
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground">
          <IconPhoto className="size-8" />
          <p className="text-sm font-medium">Select an item</p>
          <p className="text-xs">
            Choose a Book item to manage its web preview and moderation state.
          </p>
        </div>
      </aside>
    );
  }

  return (
    <aside className="flex min-h-0 w-full shrink-0 flex-col overflow-hidden border-l bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold">Web & main preview</h2>
          <p className="truncate text-xs text-muted-foreground">{item.title}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={onClose}
        >
          <IconX className="size-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-5">
          <div className="space-y-2">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Cover image
            </h3>
            <div
              role="button"
              tabIndex={0}
              className={`relative group border rounded-lg overflow-hidden cursor-pointer text-left transition-all ${
                draft.coverURL
                  ? 'hover:ring-2 ring-primary/30'
                  : 'border-dashed border-2 hover:border-primary/50'
              } ${uploadingMedia === 'cover' ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => coverInputRef.current?.click()}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  coverInputRef.current?.click();
                }
              }}
            >
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={event => {
                  const file = event.target.files?.[0];
                  if (file) onUpload(item, file, 'cover');
                  event.currentTarget.value = '';
                }}
              />
              {draft.coverURL ? (
                <div className="relative aspect-[16/10]">
                  <MediaPreview
                    title={`${item.title} cover`}
                    coverURL={draft.coverURL}
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="text-white text-[11px] font-medium flex items-center gap-1.5">
                      <IconPhoto className="h-3.5 w-3.5" />
                      Change
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-[16/10] flex flex-col items-center justify-center text-muted-foreground bg-muted/30">
                  <IconPhoto className="h-6 w-6 mb-1.5" />
                  <span className="text-[10px]">
                    {uploadingMedia === 'cover'
                      ? 'Uploading...'
                      : 'Add cover image'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-border" />

          <div className="space-y-2">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Cover video
            </h3>
            <div
              role="button"
              tabIndex={0}
              className={`relative group border rounded-lg overflow-hidden cursor-pointer text-left transition-all ${
                draft.videoURL
                  ? 'hover:ring-2 ring-primary/30'
                  : 'border-dashed border-2 hover:border-primary/50'
              } ${uploadingMedia === 'video' ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => videoInputRef.current?.click()}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  videoInputRef.current?.click();
                }
              }}
            >
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={event => {
                  const file = event.target.files?.[0];
                  if (file) onUpload(item, file, 'video');
                  event.currentTarget.value = '';
                }}
              />
              {draft.videoURL ? (
                <div className="relative aspect-[16/10]">
                  <MediaPreview
                    title={`${item.title} video`}
                    videoURL={draft.videoURL}
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="text-white text-[11px] font-medium flex items-center gap-1.5">
                      <IconPhoto className="h-3.5 w-3.5" />
                      Change
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-[16/10] flex flex-col items-center justify-center text-muted-foreground bg-muted/30">
                  <IconPhoto className="h-6 w-6 mb-1.5" />
                  <span className="text-[10px]">
                    {uploadingMedia === 'video'
                      ? 'Uploading...'
                      : 'Add cover video'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-border" />

          <details className="rounded-lg border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Advanced URLs
            </summary>
            <div className="mt-3 grid gap-3">
              <label className="grid gap-1 text-sm">
                <span className="font-medium">Cover image URL</span>
                <Input
                  value={draft.coverURL}
                  onChange={event =>
                    updateDraft({ coverURL: event.target.value })
                  }
                  placeholder="https://.../cover.png"
                />
              </label>

              <label className="grid gap-1 text-sm">
                <span className="font-medium">Video URL</span>
                <Input
                  value={draft.videoURL}
                  onChange={event =>
                    updateDraft({ videoURL: event.target.value })
                  }
                  placeholder="https://.../preview.mp4"
                />
              </label>

              <label className="grid gap-1 text-sm">
                <span className="font-medium">Preview frame URLs</span>
                <Textarea
                  value={draft.framesText}
                  onChange={event =>
                    updateDraft({ framesText: event.target.value })
                  }
                  placeholder="https://.../frame-00.png"
                  className="min-h-24 font-mono text-xs"
                />
              </label>
            </div>
          </details>

          <details className="rounded-lg border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Advanced metadata JSON
            </summary>
            <Textarea
              value={draft.assetsText}
              onChange={event =>
                updateDraft({ assetsText: event.target.value })
              }
              className="mt-3 min-h-32 font-mono text-xs"
            />
          </details>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border p-3">
        <Button
          type="button"
          variant="ghost"
          disabled={busy || !item}
          onClick={() => onClear(item)}
        >
          Clear preview
        </Button>
        <Button
          type="button"
          disabled={busy || !item}
          onClick={() => onSave(item)}
        >
          Save
        </Button>
      </div>
    </aside>
  );
}

function SiteCandidatesTable({
  items,
  ownerById,
  loading,
  pendingActionId,
  onPublish,
}: {
  items: GallerySiteCandidate[];
  ownerById: Map<string, User>;
  loading: boolean;
  pendingActionId: string | null;
  onPublish: (item: GallerySiteCandidate) => void;
}) {
  const groups = useMemo(
    () => groupSiteCandidatesByOwner(items, ownerById),
    [items, ownerById]
  );

  if (loading) {
    return (
      <div className="space-y-2">
        {loadingSkeletonRows.map(row => (
          <Skeleton key={row} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-md border border-dashed border-border p-12 text-sm text-muted-foreground">
        No active site candidates.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[320px]">Site</TableHead>
            <TableHead className="min-w-[200px]">Owner</TableHead>
            <TableHead className="min-w-[260px]">Source</TableHead>
            <TableHead>Book status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map(group => (
            <React.Fragment key={group.ownerId}>
              <OwnerGroupRow
                label={group.label}
                count={group.items.length}
                colSpan={5}
              />
              {group.items.map(item => {
                const isPending = pendingActionId === item.id;
                return (
                  <TableRow key={item.id}>
                    <TableCell className="max-w-[42ch]">
                      <div className="truncate font-medium">
                        {item.title || item.site_name || '(untitled site)'}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span
                          className="font-mono text-[11px] text-muted-foreground"
                          title={item.site_name}
                        >
                          {item.site_name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium">
                          {group.label}
                        </span>
                        <span
                          className="truncate font-mono text-[11px] text-muted-foreground"
                          title={item.owner_user_id}
                        >
                          {shortId(item.owner_user_id)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <SourceLink url={item.source_url} />
                        <span
                          className="font-mono text-[11px] text-muted-foreground"
                          title={item.agent_id}
                        >
                          agent {shortId(item.agent_id)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.already_in_book ? (
                        <div className="flex flex-col gap-1">
                          <Badge className="w-fit border-0 bg-emerald-500/15 font-medium text-emerald-600 dark:text-emerald-400">
                            In Book
                          </Badge>
                          {item.submission_status ? (
                            <span className="text-[11px] text-muted-foreground">
                              {item.submission_status}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <Badge variant="outline" className="font-medium">
                          Not published
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isPending || item.already_in_book}
                        onClick={() => onPublish(item)}
                      >
                        <IconCheck className="mr-1 size-4" />
                        Publish to Book
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
